import { AppHTTPService } from '@/api/http-service'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Input,
  Label,
} from '@flatfile/baseline'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

interface CreateSheetModalProps {
  isOpen: boolean
  onClose: () => void
  projectId: string
  fileId?: string
  onSheetCreated?: () => void
}

interface FieldDefinition {
  id: string
  key: string
  label: string
  type: 'string' | 'number' | 'boolean' | 'date'
  required: boolean
}

export function CreateSheetModal({
  isOpen,
  onClose,
  projectId,
  fileId,
  onSheetCreated,
}: CreateSheetModalProps) {
  const queryClient = useQueryClient()
  const [sheetName, setSheetName] = useState('New Sheet')
  const [fields, setFields] = useState<FieldDefinition[]>([
    {
      id: '1',
      key: 'name',
      label: 'Name',
      type: 'string',
      required: true,
    },
  ])

  const createSheetMutation = useMutation({
    mutationFn: async () => {
      // First, create a file if we don't have one
      let actualFileId = fileId
      if (!actualFileId) {
        // For now, we'll create a file with a CSV header based on the fields
        const fileName = sheetName.endsWith('.csv')
          ? sheetName
          : `${sheetName}.csv`

        // Create CSV content with headers based on fields
        const headers = fields.map((field) => `"${field.label}"`).join(',')
        const csvContent = headers + '\n'

        const emptyFile = new File([csvContent], fileName, {
          type: 'text/csv',
        })

        try {
          const fileResponse: any = await AppHTTPService.client.files.create(
            emptyFile,
            '', // environmentId
            projectId,
            'manual' // origin
          )
          // The response should contain the file data
          actualFileId = fileResponse.id || fileResponse.data?.id

          if (!actualFileId) {
            throw new Error('Failed to create file: No file ID returned')
          }
        } catch (error) {
          console.error('File creation error:', error)
          throw new Error(`Failed to create file: ${error.message}`)
        }
      }

      // Create the sheet with the defined schema
      const schema = {
        fields: fields.map((field) => ({
          key: field.key,
          label: field.label,
          type: field.type,
          constraints: field.required ? [{ type: 'required' }] : [],
        })),
      }

      const sheetResponse: any = await AppHTTPService.client.sheets.create({
        fileId: actualFileId,
        name: sheetName,
        schema,
      })

      return {
        fileId: actualFileId,
        sheetId: sheetResponse.data?.id || sheetResponse.id,
      }
    },
    onSuccess: () => {
      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['files', projectId] })
      queryClient.invalidateQueries({ queryKey: ['sheets'] })
      onSheetCreated?.()
      handleClose()
    },
    onError: (error: Error) => {
      console.error('Failed to create sheet:', error)
      // You could add a toast notification here
      alert(`Failed to create sheet: ${error.message}`)
    },
  })

  const handleClose = () => {
    setSheetName('New Sheet')
    setFields([
      {
        id: '1',
        key: 'name',
        label: 'Name',
        type: 'string',
        required: true,
      },
    ])
    onClose()
  }

  const addField = () => {
    const newField: FieldDefinition = {
      id: Date.now().toString(),
      key: `field_${fields.length + 1}`,
      label: `Field ${fields.length + 1}`,
      type: 'string',
      required: false,
    }
    setFields([...fields, newField])
  }

  const updateField = (id: string, updates: Partial<FieldDefinition>) => {
    setFields(
      fields.map((field) =>
        field.id === id ? { ...field, ...updates } : field
      )
    )
  }

  const removeField = (id: string) => {
    if (fields.length > 1) {
      setFields(fields.filter((field) => field.id !== id))
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className='sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Create New Sheet</DialogTitle>
          <DialogDescription>
            Define the structure for your new sheet by adding fields.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-4'>
          <div>
            <Label htmlFor='sheetName'>Sheet Name</Label>
            <Input
              id='sheetName'
              value={sheetName}
              onChange={(e) => setSheetName(e.target.value)}
              placeholder='Enter sheet name'
            />
          </div>

          <div>
            <div className='flex items-center justify-between mb-2'>
              <Label>Fields</Label>
              <Button
                variant='outline'
                size='sm'
                icon='Plus'
                onClick={addField}
              >
                Add Field
              </Button>
            </div>

            <div className='space-y-2 max-h-80 overflow-y-auto'>
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className='flex items-center gap-2 p-3 border rounded-lg bg-gray-50'
                >
                  <div className='flex-1 grid grid-cols-3 gap-2'>
                    <Input
                      value={field.label}
                      onChange={(e) =>
                        updateField(field.id, {
                          label: e.target.value,
                          key: e.target.value
                            .toLowerCase()
                            .replace(/\s+/g, '_'),
                        })
                      }
                      placeholder='Field label'
                    />
                    <select
                      value={field.type}
                      onChange={(e) =>
                        updateField(field.id, {
                          type: e.target.value as FieldDefinition['type'],
                        })
                      }
                      className='px-3 py-2 border rounded-md'
                    >
                      <option value='string'>Text</option>
                      <option value='number'>Number</option>
                      <option value='boolean'>Boolean</option>
                      <option value='date'>Date</option>
                    </select>
                    <label className='flex items-center gap-2'>
                      <input
                        type='checkbox'
                        checked={field.required}
                        onChange={(e) =>
                          updateField(field.id, {
                            required: e.target.checked,
                          })
                        }
                      />
                      Required
                    </label>
                  </div>
                  <Button
                    variant='ghost'
                    size='icon-sm'
                    onClick={() => removeField(field.id)}
                    disabled={fields.length === 1}
                  >
                    <Icon name='x' size={16} />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={() => createSheetMutation.mutate()}
            loading={createSheetMutation.isPending}
            disabled={!sheetName.trim() || fields.length === 0}
          >
            Create Sheet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
